import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Lines, EASE } from "../site/Motion";

export function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const setVideoRef = (el) => {
    videoRef.current = el;
    if (el) {
      el.muted = true;
      el.setAttribute("muted", "");
      el.setAttribute("playsinline", "");
      el.setAttribute("webkit-playsinline", "");
    }
  };

  useEffect(() => {
    const conn = navigator.connection || {};
    if (conn.saveData || /(^|\s)2g/.test(conn.effectiveType || "")) return;
    setVideoSrc(
      window.innerWidth < 768
        ? { webm: "/assets/hero-720.webm", mp4: "/assets/hero-720.mp4" }
        : { webm: "/assets/hero-1080.webm", mp4: "/assets/hero-1080.mp4" }
    );
  }, []);

  useEffect(() => {
    if (!videoSrc || !videoRef.current) return;
    const v = videoRef.current;
    v.load();
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);
    return () => {
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
    };
  }, [videoSrc]);

  useEffect(() => {
    const onTouch = () => {
      const v = videoRef.current;
      if (v && v.paused) v.play().catch(() => {});
    };
    window.addEventListener("touchstart", onTouch, { once: true, passive: true });
    return () => window.removeEventListener("touchstart", onTouch);
  }, []);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="theme-dark relative flex min-h-[100svh] items-end overflow-hidden bg-ink">
      <motion.div className="absolute inset-0" style={reduce ? {} : { y: imgY, scale: imgScale }}>
        <motion.video
          ref={setVideoRef}
          poster="/assets/hero-poster.jpg"
          autoPlay
          muted
          defaultMuted
          loop
          playsInline
          preload="auto"
          onCanPlay={() => videoRef.current?.play().catch(() => {})}
          aria-label="SWAPA, quadriciclo elettrico L6e in un contesto urbano"
          className="h-full w-full object-cover"
          initial={reduce ? false : { scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
        >
          {videoSrc && (
            <>
              <source src={videoSrc.webm} type="video/webm" />
              <source src={videoSrc.mp4} type="video/mp4" />
            </>
          )}
        </motion.video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/30" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-transparent" aria-hidden="true" />

      <motion.div style={reduce ? {} : { y: textY, opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-28 pt-40 sm:px-10 sm:pb-32">
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow"
        >
          Swapa™ — 100% elettrica
        </motion.p>

        <h1 className="mt-6 font-display font-black uppercase leading-[0.88] tracking-tighter">
          <Lines
            animate="load"
            delay={0.35}
            lines={["The Urban"]}
            className="text-[13.5vw] text-white sm:text-[11vw] lg:text-[8.5rem]"
          />
          <Lines
            animate="load"
            delay={0.55}
            lines={["Lifestyle Icon."]}
            className="text-[13.5vw] text-white sm:text-[11vw] lg:text-[8.5rem]"
          />
        </h1>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          {["100% Electric", "ONE+1™", "Ready to drive"].map((b) => (
            <span key={b} className="border border-white/20 bg-black/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-200 backdrop-blur-sm">
              {b}
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <Link
            to="/preordina"
            data-testid="hero-preorder-cta"
            className="group inline-flex items-center justify-center gap-3 bg-swapa-yellow px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5"
          >
            Pre-ordina — €200
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/test-drive"
            data-testid="hero-testdrive-cta"
            className="group inline-flex items-center justify-center gap-3 border border-white/30 px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-swapa-yellow hover:text-swapa-yellow"
          >
            Prenota un test drive
          </Link>
          <button
            data-testid="hero-discover-link"
            onClick={() => document.getElementById("manifesto")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 transition-colors hover:text-white sm:ml-4"
          >
            Scopri SWAPA
            <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
        </motion.div>

        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400"
        >
          Da €13.990 IVA inclusa
        </motion.p>
      </motion.div>
    </section>
  );
}
